import { useEffect, useId, useRef, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { localeTagFor } from "@/i18n/locale";

export interface ConnectionHistoryPanelProps {
  provider: "strava" | "apple_health" | "health_connect";
  /** Pass only provider_metadata.history_sync; the parent keys this panel by connection ID. */
  history?: unknown;
  onImport: (from: string) => Promise<void>;
  onContinue: () => Promise<void>;
  busy: boolean;
  /** Includes unavailable connection reads and revoked/disconnected accounts. */
  disabled?: boolean;
}

type HistoryStatus = "in_progress" | "complete" | "partial" | "blocked";
interface HistoryProgress {
  from: string;
  to: string;
  next_before: string;
  completed_windows: number;
  status: HistoryStatus;
  timezone: string;
  visibilityUnknown: boolean;
  window?: { from: string; to: string; complete: boolean };
}

const MIN_DAY = "1970-01-01";
const STATUS_KEYS: Record<HistoryStatus, string> = {
  in_progress: "connections.history.status.in_progress",
  complete: "connections.history.status.complete",
  partial: "connections.history.status.partial",
  blocked: "connections.history.status.blocked",
};

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown> : null;
}

function utcDate(value: unknown): string | null {
  if (typeof value !== "string" || value.length > 32 ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/.test(value)) return null;
  const timestamp = Date.parse(value);
  // Native history starts at local midnight: January 1, 1970 may be December 31, 1969 in UTC.
  if (!Number.isFinite(timestamp) || timestamp < -86_400_000 || timestamp > Date.now() + 86_400_000) return null;
  // Date.parse normalizes impossible dates (e.g. February 30); never display one as genuine progress.
  return new Date(timestamp).toISOString().slice(0, 19) === value.slice(0, 19) ? value : null;
}

function historyTimezone(value: unknown): string {
  if (typeof value !== "string" || value.length > 100) return "UTC";
  try {
    return new Intl.DateTimeFormat(undefined, { timeZone: value }).resolvedOptions().timeZone;
  } catch {
    // A corrupt optional timezone must not hide otherwise valid persisted progress.
    return "UTC";
  }
}

function readProgress(value: unknown): HistoryProgress | null {
  const raw = record(value);
  if (!raw || raw.version !== 1) return null;
  const from = utcDate(raw.from), to = utcDate(raw.to), next = utcDate(raw.next_before);
  const count = raw.completed_windows;
  if (!from || !to || !next || Date.parse(from) >= Date.parse(to) ||
    Date.parse(next) < Date.parse(from) || Date.parse(next) > Date.parse(to) ||
    typeof count !== "number" || !Number.isSafeInteger(count) || count < 0 || count > 100_000 ||
    typeof raw.status !== "string" || !Object.prototype.hasOwnProperty.call(STATUS_KEYS, raw.status)) return null;
  const status = raw.status as HistoryStatus;
  if (status === "complete" && Date.parse(next) !== Date.parse(from)) return null;
  const coverage = record(raw.coverage_by_type);
  const visibilityUnknown = raw.permission_evidence === "ios_read_grant_unobservable" ||
    raw.visibility === "unknown" || ["sleep", "workouts", "steps", "heart_rate", "hrv"].some(type => {
      const reason = record(coverage?.[type])?.reason;
      return reason === "ios_empty_or_read_denied" || reason === "empty_or_read_denied";
    });
  const progress: HistoryProgress = { from, to, next_before: next, completed_windows: count, status,
    timezone: historyTimezone(raw.timezone), visibilityUnknown };
  const window = record(raw.window);
  const windowFrom = utcDate(window?.from), windowTo = utcDate(window?.to);
  if (windowFrom && windowTo && typeof window?.complete === "boolean" &&
    Date.parse(windowFrom) >= Date.parse(from) && Date.parse(windowTo) <= Date.parse(to) &&
    Date.parse(windowFrom) < Date.parse(windowTo)) {
    progress.window = { from: windowFrom, to: windowTo, complete: window.complete };
  }
  // Provider reasons, type coverage and unknown fields are never rendered as arbitrary UI text.
  return progress;
}

function todayUtc(): string { return new Date().toISOString().slice(0, 10); }
function defaultFrom(): string {
  const date = new Date();
  date.setUTCFullYear(date.getUTCFullYear() - 1);
  return date.toISOString().slice(0, 10);
}
function validDay(day: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || day < MIN_DAY || day > todayUtc()) return false;
  const timestamp = Date.parse(day + "T00:00:00.000Z");
  return Number.isFinite(timestamp) && new Date(timestamp).toISOString().slice(0, 10) === day;
}

export default function ConnectionHistoryPanel({
  provider, history, onImport, onContinue, busy, disabled = false,
}: ConnectionHistoryPanelProps) {
  const { t, tf, lang } = useLanguage();
  const formId = useId();
  const [expanded, setExpanded] = useState(false);
  const [from, setFrom] = useState(defaultFrom);
  const [working, setWorking] = useState(false);
  const [errorKey, setErrorKey] = useState("");
  const locked = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const progress = readProgress(history);
  const malformed = history != null && !progress;
  const unavailable = disabled || busy || working;
  const formatDate = (date: string) => new Intl.DateTimeFormat(localeTagFor(lang), {
    dateStyle: "medium", timeZone: progress?.timezone ?? "UTC",
  }).format(new Date(date));

  const run = async (action: () => Promise<void>) => {
    if (locked.current || busy || disabled) return;
    locked.current = true;
    setWorking(true);
    setErrorKey("");
    try {
      // Each click submits exactly one bounded provider request; the parent refreshes persisted progress.
      await action();
    } catch {
      console.warn("[ConnectionHistoryPanel] history request failed", { provider });
      if (mounted.current) setErrorKey("connections.history.actionFailed");
    } finally {
      locked.current = false;
      if (mounted.current) setWorking(false);
    }
  };

  const start = () => {
    if (locked.current || busy || disabled) return;
    if (!validDay(from)) { setErrorKey("connections.history.invalidDate"); return; }
    void run(() => onImport(from + "T00:00:00.000Z"));
  };

  return (
    <section className="space-y-3 rounded-xl border border-border/70 bg-muted/20 px-3 py-3" data-provider-history={provider}>
      <button type="button" aria-expanded={expanded} aria-controls={formId}
        onClick={() => setExpanded(!expanded)}
        className="flex min-h-11 w-full items-center justify-between gap-3 text-left text-sm font-semibold">
        {t("connections.history.title")}
        <ChevronDown aria-hidden="true" className={`h-4 w-4 shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
      <p className="text-xs text-muted-foreground">{t("connections.history.hint")}</p>

      {progress && (
        <div className="space-y-1.5 text-xs" aria-live="polite">
          <p role="status" className={`font-medium ${progress.status === "partial" || progress.status === "blocked" ? "text-amber-500" : "text-foreground"}`}>
            {t(STATUS_KEYS[progress.status])}
          </p>
          <p className="text-muted-foreground">{tf("connections.history.interval", { from: formatDate(progress.from), to: formatDate(progress.to) })}</p>
          <p className="text-muted-foreground">{tf("connections.history.windows", { count: progress.completed_windows })}</p>
          {progress.status !== "complete" && <p className="text-muted-foreground">{tf("connections.history.cursor", { date: formatDate(progress.next_before) })}</p>}
          {progress.window && <>
            <p className="text-muted-foreground">{tf("connections.history.window", { from: formatDate(progress.window.from), to: formatDate(progress.window.to) })}</p>
            <p className="text-muted-foreground">{t(progress.window.complete ? "connections.history.windowComplete" : "connections.history.windowPartial")}</p>
          </>}
          {progress.status === "partial" && <p className="text-amber-500">{t("connections.history.partialHint")}</p>}
          {progress.status === "blocked" && <p className="text-amber-500">{t("connections.history.blockedHint")}</p>}
          {progress.visibilityUnknown && <p className="text-amber-500">{t("connections.history.visibilityUnknown")}</p>}
        </div>
      )}
      {malformed && <p role="alert" className="text-xs text-amber-500">{t("connections.history.malformed")}</p>}

      {expanded && <div id={formId} className="space-y-2">
        <label htmlFor={formId + "-from"} className="block text-xs font-medium">{t("connections.history.from")}</label>
        <input id={formId + "-from"} type="date" value={from} min={MIN_DAY} max={todayUtc()}
          disabled={unavailable} onChange={event => { setFrom(event.target.value); setErrorKey(""); }}
          className="min-h-11 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm disabled:opacity-50" />
        <button type="button" onClick={start} disabled={unavailable}
          className="min-h-11 w-full rounded-lg bg-primary px-3 py-2.5 text-sm font-medium text-primary-foreground disabled:opacity-50">
          {t("connections.history.start")}
        </button>
      </div>}
      {progress && progress.status !== "complete" && (
        <button type="button" onClick={() => { void run(onContinue); }} disabled={unavailable}
          className="min-h-11 w-full rounded-lg border border-primary/30 bg-primary/10 px-3 py-2.5 text-sm font-medium text-primary disabled:opacity-50">
          {t("connections.history.continue")}
        </button>
      )}
      {(working || busy) && <p role="status" className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />{t("connections.history.working")}
      </p>}
      {errorKey && <p role="alert" className="text-xs text-destructive">{t(errorKey)}</p>}
    </section>
  );
}
